import React, {useCallback, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, IconButton, Typography} from "@mui/material";
import Button from "../../../../../../../components/base/button/button";
import {ArrowBack, DownloadRounded} from "@mui/icons-material";
import ImportCsvFile, {ImportCsvFileSteps} from "../../../../../../../components/app-specific/import-csv-file";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Utils from "../../../../../../../../core/services/utils/utils";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import Api from "../../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../../core/services/toast";


const initialCsvFileState = {
    step: ImportCsvFileSteps.dropzone,
    parsedColumns: [],
    parsedRows: [],
}

const ProductVarianceImportDialogFileImport = (props) => {
    const {onImport, closeDialog} = props;
    const formId = useMemo(() => Utils.createUUID(), []);
    const [importCsvFileState, setImportCsvFileState] = useState(initialCsvFileState);
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();

    const fields = useMemo(() => [
        {
            name: 'partNo',
            title: 'Part number',
            generator: () => '',
            format: (e) => e?.toString()?.toUpperCase()?.trim(),
        },
    ], [])

    /**
     * Callback function passed to the column mapper
     */
    const onDropzoneParsed = useCallback(({rows, columns}) => {
        setImportCsvFileState({
            step: ImportCsvFileSteps.columnMapper,
            parsedColumns: columns,
            parsedRows: rows,
        });
    }, [])

    /**
     * Validates the items in the file and returns the valid items.
     */
    const validateItems = useCallback(async (_parsedRows) => {
        // remove empty part numbers.
        const parsedRows = _parsedRows.filter(e => !!e.partNo);

        setImporting(true);
        const validationResponse = await Api.validatePartNumbers({
            partNumbers: Utils.getUniqueEntries(parsedRows, e => e.partNo),
        })
        if (!isMounted())
            return;

        setImporting(false);

        if (!validationResponse?.resultFlag) {
            ToastService.error({
                title: "Error",
                message: "It seems that none of the provided part numbers are valid. please check your file and try again.",
            })
            return
        }

        let validEntries = parsedRows.filter((e) => !!validationResponse.data[e.partNo]);
        let invalidEntries = parsedRows.filter((e) => !validationResponse.data[e.partNo]).map(e => ({
            ...e,
            error: {partNo: true},
        }));
        if (!validEntries.length) {
            ToastService.warning({
                title: "Warning",
                message: "No valid part numbers found. Please check the part numbers and try again. If you believe this is an error, please contact support.",
            })
            return
        }

        return [validEntries, invalidEntries];
    }, [isMounted])

    /**
     * Callback function passed to the column mapper. Validates the items and sets the review section state.
     */
    const onColumnMapperParsed = useCallback(async (parsedRows) => {
        const validationResult = await validateItems(parsedRows);
        if (!isMounted())
            return;
        if (!validationResult) {
            setImportCsvFileState(initialCsvFileState);
            return;
        }
        const [validEntries, invalidEntries] = validationResult;
        if (!!invalidEntries?.length) {
            const downloadInvalidEntries = () => {
                const csvRecords = invalidEntries.map(e => ({
                    "Part Number": e.partNo,
                }));
                Utils.exportCsvFile(csvRecords, {fileTitle: "invalid-part-numbers.csv"});
            }
            ToastService.warning({
                title: "Some Invalid Entries",
                message: "Some of the provided part numbers are invalid. You can review the invalid entries by downloading this file.",
                actions: (
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        color={'primary'}
                        onClick={downloadInvalidEntries}
                        startIcon={<DownloadRounded/>}
                    >
                        Download Invalid Entries
                    </Button>
                )
            });
        }
        if (validEntries.length) {
            closeDialog();
            onImport?.(validEntries);
        }
    }, [isMounted, onImport, validateItems, closeDialog])

    return (
        <>
            <DialogContent>
                {
                    importing &&
                    <LoadingLottieAnimation width={'80%'} className={''}/>
                }
                {
                    !importing &&
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%'}}>
                            {
                                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                                <>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                                        <IconButton
                                            onClick={() => setImportCsvFileState(initialCsvFileState)}
                                            color={'paper.secondary'}
                                        >
                                            <ArrowBack className={'icon normal'}/>
                                        </IconButton>
                                        <Typography variant={'body1'} fontWeight={600}>
                                            Column Mapping
                                        </Typography>
                                    </Box>
                                    <Typography variant={'body2'} sx={{marginBlockStart: 1, marginBlockEnd: 4, width: '100%'}}>
                                        Please map the columns to the fields so we can process the adjustments.
                                    </Typography>
                                </>
                            }
                            <ImportCsvFile
                                dropzoneNotes={undefined}
                                columnMapperInitialValues={undefined}
                                step={importCsvFileState.step}
                                fields={fields}
                                sampleFileTitle={"product-variants-import-sample-file.csv"}
                                parsedColumns={importCsvFileState.parsedColumns}
                                parsedRows={importCsvFileState.parsedRows}
                                onDropzoneParsed={onDropzoneParsed}
                                onColumnMapperParsed={onColumnMapperParsed}
                                formId={formId}
                            />
                        </Box>
                    </>
                }
            </DialogContent>
            {
                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0, m: 0}}>
                    <Button
                        variant={'dialog'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={() => setImportCsvFileState(initialCsvFileState)}
                    >
                        Back
                    </Button>
                    <Button
                        autoFocus
                        variant={'dialog'}
                        size={'medium'}
                        color={'primary'}
                        type={'submit'}
                        borderRadius={'8px 0 8px 0'}
                        form={formId}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            }
        </>
    );
}

export default ProductVarianceImportDialogFileImport;